/**
 * LOOKUP QUERY POLICY — keys, deduplication and invalidation (#2217).
 *
 * Root's ruling (66585): *"Do NOT add React Query or QueryClient custody to
 * `packages/ui`. Keep Lookup presentational and provider-agnostic. Put query
 * keys, deduplication, invalidation and `useQuery` orchestration in
 * `packages/experience` when reusable across hosts, or in the host adapter when
 * host-specific."*
 *
 * ── WHERE THE LINE FALLS, AND WHY ───────────────────────────────────────────
 * `packages/experience` imports React **nowhere** and depends on `@solum/sdk`
 * alone — measured, not assumed. Putting `useQuery` here would add both React
 * and React Query to a package that has neither, which is the same objection
 * that kept them out of `packages/ui`, one tier over.
 *
 * So the ruling's two halves split cleanly:
 *
 *   · **reusable across hosts** → this file: what a query is CALLED, when two
 *     questions are the SAME question, and what a write INVALIDATES. All pure,
 *     all framework-agnostic, identical in Console and Portal.
 *   · **host-specific** → `apps/console/src/data/useLookupSearch.ts`: the
 *     `useQuery` call itself, where React Query already lives.
 *
 * A host that never adopts React Query can still use every rule below.
 *
 * ── WHY A KEY IS NOT JUST THE TEXT ──────────────────────────────────────────
 * A lookup question is the text AND the facet AND the type AND the advanced
 * filters. `Lookup` already knows this — its own effect re-runs on
 * `[q, facet, type, filters]`, and a comment there records the defect that
 * taught it: leaving `filters` out kept a refusal from one attempt visible over
 * the results of a different one. The cache key has to carry exactly the same
 * four, or two genuinely different questions share an answer.
 */

/** Everything that makes a lookup question the question it is. */
export interface LookupQuestion {
  /** The registry or relation being searched. */
  source: string
  /** The typed text. */
  query: string
  /** An optional facet within the source. */
  facet?: string | null
  /** For a polymorphic lookup, which entity type is being searched. */
  type?: string | null
  /** The advanced filter payload, as the grid/MCP shape. */
  filters?: unknown
}

/**
 * A stable cache key.
 *
 * Two properties matter and both are asserted by the tests:
 *
 *   1. **Stable** — the same question produces the same key every time,
 *      whatever the insertion order of the filter object. An unstable key is a
 *      cache that never hits, which looks like a slow product rather than a bug.
 *   2. **Discriminating** — two different questions never collide. A collision
 *      is worse than a miss: it serves one search's answer to another search.
 *
 * The query text is trimmed and lower-cased because `Ada` and ` ada ` are the
 * same question to every source this feeds; the filter payload is serialised
 * with sorted keys so `{a,b}` and `{b,a}` do not split the cache.
 */
export function lookupQueryKey(question: LookupQuestion): readonly unknown[] {
  return [
    'lookup',
    question.source,
    normaliseQuery(question.query),
    question.facet ?? null,
    question.type ?? null,
    stableSerialise(question.filters ?? null),
  ]
}

/** `Ada ` and `ada` are the same question. */
export const normaliseQuery = (query: string): string => query.trim().toLocaleLowerCase()

/**
 * JSON with object keys in a deterministic order.
 *
 * `JSON.stringify` preserves insertion order, so a filter builder that emits
 * `{ field, op }` on one render and `{ op, field }` on the next would produce
 * two keys for one question — a cache that silently never hits.
 */
export function stableSerialise(value: unknown): string {
  const walk = (input: unknown): unknown => {
    if (input === null || typeof input !== 'object') return input
    if (Array.isArray(input)) return input.map(walk)
    const source = input as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(source).sort()) out[key] = walk(source[key])
    return out
  }
  return JSON.stringify(walk(value))
}

/** Are these the same question, and therefore one request rather than two? */
export function isSameQuestion(a: LookupQuestion, b: LookupQuestion): boolean {
  return stableSerialise(lookupQueryKey(a)) === stableSerialise(lookupQueryKey(b))
}

/**
 * Should this question be ASKED at all?
 *
 * Below the minimum the answer is no, and that is not a cache decision — an
 * unsent request cannot be stale, cannot race and cannot refuse. Keeping the
 * rule here rather than in the hook means Console and Portal cannot disagree
 * about when a governed door starts being called.
 */
export function shouldAsk(question: LookupQuestion, minChars: number): boolean {
  return normaliseQuery(question.query).length >= Math.max(0, minChars)
}

/**
 * What a write to `entity` makes stale.
 *
 * Returned as key PREFIXES: a host matches them however its cache does. This is
 * the half a hand-rolled effect has never had — `Lookup` re-runs its search when
 * the question changes and has no idea that a record was created elsewhere, so
 * a freshly created row does not appear in the next lookup until the text
 * changes. Naming the invalidation here lets a host fix that without `Lookup`
 * learning what a cache is.
 */
export function lookupInvalidations(entity: string): readonly (readonly unknown[])[] {
  return [
    ['lookup', entity],
    // A polymorphic lookup is keyed by the SOURCE, which may be a relation
    // rather than the entity; a write to the entity still makes it stale.
    ['lookup'],
  ]
}

/**
 * How long an answer stays fresh, in milliseconds.
 *
 * Deliberately short. A lookup is a picker over data other people are editing,
 * so a long freshness window means offering a choice that no longer exists —
 * and the cost of being wrong here is a refused write, not a slow one.
 */
export const LOOKUP_STALE_MS = 30_000

/** How long an unused answer is kept before eviction. */
export const LOOKUP_GC_MS = 5 * 60_000
